import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Typography,
  Button,
  Paper,
  TextField,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Chip,
  Menu,
  MenuItem,
  Alert,
  CircularProgress,
} from '@mui/material';
import {
  Add as AddIcon,
  Delete as DeleteIcon,
  Edit as EditIcon,
  Download as DownloadIcon,
  AutoAwesome as GenerateIcon,
  AddCircle as AddCircleIcon,
  MoreVert as MoreVertIcon,
  Logout as LogoutIcon,
  Save as SaveIcon,
} from '@mui/icons-material';
import SchoolIcon from '@mui/icons-material/School';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import * as XLSX from 'xlsx';
import { generateAiRecord, saveWorkspace, loadWorkspace } from '../services/api';

// 테이블 데이터 타입 정의
interface StudentRecord {
  id: string;
  studentNumber: string;
  studentName: string;
  subject: string;
  assignment: string;
  studentResult: string;
  [key: string]: any; // 동적 컬럼을 위한 인덱스 시그니처
  generatedContent: string;
  charCount: number;
}

interface CustomColumn {
  id: string;
  name: string;
  type: 'text' | 'number';
}

const AiRecordPage: React.FC = () => {
  const navigate = useNavigate();
  const [user, setUser] = useState<{ email: string; name: string } | null>(null);
  const [records, setRecords] = useState<StudentRecord[]>([]);
  const [customColumns, setCustomColumns] = useState<CustomColumn[]>([]);
  const [isGenerating, setIsGenerating] = useState<{ [key: string]: boolean }>({});
  const [columnDialogOpen, setColumnDialogOpen] = useState(false);
  const [newColumnName, setNewColumnName] = useState('');
  const [editingCell, setEditingCell] = useState<{ recordId: string; field: string } | null>(null);
  const [tempValue, setTempValue] = useState('');
  const [menuAnchor, setMenuAnchor] = useState<HTMLElement | null>(null);
  const [selectedRecord, setSelectedRecord] = useState<string | null>(null);
  const [editContentDialogOpen, setEditContentDialogOpen] = useState(false);
  const [editingContent, setEditingContent] = useState('');
  const [editingContentId, setEditingContentId] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  const autoSavingRef = useRef(false);

  // 작업공간 저장 함수
  const saveWorkspaceData = useCallback(async () => {
    if (!user || isSaving) return;
    
    setIsSaving(true);
    try {
      await saveWorkspace({
        user_email: user.email,
        workspace_name: "기본 작업공간",
        records: records,
        custom_columns: customColumns
      });
      console.log('작업공간 저장 완료');
    } catch (error) {
      console.error('작업공간 저장 실패:', error);
    } finally {
      setIsSaving(false);
    }
  }, [user, isSaving]); // records와 customColumns 제거

  // 작업공간 불러오기 함수
  const loadWorkspaceData = useCallback(async () => {
    if (!user) return;
    
    try {
      const response = await loadWorkspace(user.email);
      if (response.success && response.workspace) {
        setRecords(response.workspace.records || []);
        setCustomColumns(response.workspace.custom_columns || []);
        console.log('작업공간 불러오기 완료');
      } else {
        // 기존 작업공간이 없으면 기본 10개 행 생성
        const defaultRecords: StudentRecord[] = Array.from({ length: 10 }, (_, index) => ({
          id: `record-${index + 1}`,
          studentNumber: '',
          studentName: '',
          subject: '',
          assignment: '',
          studentResult: '',
          generatedContent: '',
          charCount: 0,
        }));
        setRecords(defaultRecords);
      }
    } catch (error) {
      console.error('작업공간 불러오기 실패:', error);
      // 오류 발생 시 기본 10개 행 생성
      const defaultRecords: StudentRecord[] = Array.from({ length: 10 }, (_, index) => ({
        id: `record-${index + 1}`,
        studentNumber: '',
        studentName: '',
        subject: '',
        assignment: '',
        studentResult: '',
        generatedContent: '',
        charCount: 0,
      }));
      setRecords(defaultRecords);
    } finally {
      setIsLoading(false);
    }
  }, [user]);

  // 컴포넌트 마운트 시 로그인 확인 및 작업공간 불러오기
  useEffect(() => {
    const userData = localStorage.getItem('ai_record_user');
    if (!userData) {
      navigate('/');
      return;
    }
    const parsedUser = JSON.parse(userData);
    setUser(parsedUser);
  }, [navigate]);

  // 사용자 정보가 설정된 후 작업공간 불러오기
  useEffect(() => {
    if (user && isLoading) {
      loadWorkspaceData();
    }
  }, [user, isLoading, loadWorkspaceData]);

  // 데이터 변경 시 자동 저장 (debounced)
  useEffect(() => {
    if (!user || isLoading) return;
    
    const saveTimeout = setTimeout(async () => {
      if (autoSavingRef.current) return;
      
      autoSavingRef.current = true;
      try {
        await saveWorkspace({
          user_email: user.email,
          workspace_name: "기본 작업공간",
          records: records,
          custom_columns: customColumns
        });
        console.log('작업공간 자동 저장 완료');
      } catch (error) {
        console.error('작업공간 자동 저장 실패:', error);
      } finally {
        autoSavingRef.current = false;
      }
    }, 2000); // 2초 후 저장
    
    return () => clearTimeout(saveTimeout);
  }, [records, customColumns, user, isLoading]);

  // 로그아웃 처리
  const handleLogout = async () => {
    // 로그아웃 전 작업공간 저장
    if (user && !isSaving) {
      await saveWorkspaceData();
    }
    localStorage.removeItem('ai_record_user');
    navigate('/');
  };

  // 셀 편집 시작
  const handleCellEdit = (recordId: string, field: string, currentValue: string) => {
    setEditingCell({ recordId, field });
    setTempValue(currentValue);
  };

  // 다음 칸으로 이동
  const moveToNextCell = () => {
    if (!editingCell) return;
    
    const currentRecordIndex = records.findIndex(r => r.id === editingCell.recordId);
    const currentRecord = records[currentRecordIndex];
    
    // 편집 가능한 필드 순서 정의
    const editableFields = ['studentNumber', 'studentName', 'subject', 'assignment', 'studentResult', ...customColumns.map(col => col.id)];
    const currentFieldIndex = editableFields.indexOf(editingCell.field);
    
    // 현재 값 저장
    setRecords(prev => prev.map(record => 
      record.id === editingCell.recordId 
        ? { ...record, [editingCell.field]: tempValue }
        : record
    ));
    
    // 다음 칸 결정
    if (currentFieldIndex < editableFields.length - 1) {
      // 같은 행의 다음 칸
      const nextField = editableFields[currentFieldIndex + 1];
      const nextValue = currentRecord[nextField] || '';
      setEditingCell({ recordId: editingCell.recordId, field: nextField });
      setTempValue(nextValue);
    } else {
      // 다음 행의 첫 번째 칸
      if (currentRecordIndex < records.length - 1) {
        const nextRecord = records[currentRecordIndex + 1];
        const firstField = editableFields[0];
        const nextValue = nextRecord[firstField] || '';
        setEditingCell({ recordId: nextRecord.id, field: firstField });
        setTempValue(nextValue);
      } else {
        // 마지막 행의 마지막 칸이면 새 행 추가 후 해당 행의 첫 번째 칸으로 이동
        const newId = `record-${records.length + 1}`;
        const newRecord: StudentRecord = {
          id: newId,
          studentNumber: '',
          studentName: '',
          subject: '',
          assignment: '',
          studentResult: '',
          generatedContent: '',
          charCount: 0,
        };
        
        // 기존 커스텀 컬럼들에 빈 값 추가
        customColumns.forEach(col => {
          newRecord[col.id] = '';
        });
        
        setRecords(prev => [...prev, newRecord]);
        
        // 새 행의 첫 번째 칸으로 이동
        const firstField = editableFields[0];
        setEditingCell({ recordId: newId, field: firstField });
        setTempValue('');
      }
    }
  };

  // 이전 칸으로 이동
  const moveToPrevCell = () => {
    if (!editingCell) return;
    
    const currentRecordIndex = records.findIndex(r => r.id === editingCell.recordId);
    const currentRecord = records[currentRecordIndex];
    
    // 편집 가능한 필드 순서 정의
    const editableFields = ['studentNumber', 'studentName', 'subject', 'assignment', 'studentResult', ...customColumns.map(col => col.id)];
    const currentFieldIndex = editableFields.indexOf(editingCell.field);
    
    // 현재 값 저장
    setRecords(prev => prev.map(record => 
      record.id === editingCell.recordId 
        ? { ...record, [editingCell.field]: tempValue }
        : record
    ));
    
    // 이전 칸 결정
    if (currentFieldIndex > 0) {
      // 같은 행의 이전 칸
      const prevField = editableFields[currentFieldIndex - 1];
      const prevValue = currentRecord[prevField] || '';
      setEditingCell({ recordId: editingCell.recordId, field: prevField });
      setTempValue(prevValue);
    } else {
      // 이전 행의 마지막 칸
      if (currentRecordIndex > 0) {
        const prevRecord = records[currentRecordIndex - 1];
        const lastField = editableFields[editableFields.length - 1];
        const prevValue = prevRecord[lastField] || '';
        setEditingCell({ recordId: prevRecord.id, field: lastField });
        setTempValue(prevValue);
      }
      // 첫 번째 행의 첫 번째 칸이면 이동하지 않음
    }
  };

  // 셀 편집 저장
  const handleCellSave = () => {
    if (!editingCell) return;
    
    setRecords(prev => prev.map(record => 
      record.id === editingCell.recordId 
        ? { ...record, [editingCell.field]: tempValue }
        : record
    ));
    setEditingCell(null);
    setTempValue('');
  };

  // 셀 편집 취소
  const handleCellCancel = () => {
    setEditingCell(null);
    setTempValue('');
  };

  // 새로운 행 추가
  const handleAddRow = () => {
    const newId = `record-${records.length + 1}`;
    const newRecord: StudentRecord = {
      id: newId,
      studentNumber: '',
      studentName: '',
      subject: '',
      assignment: '',
      studentResult: '',
      generatedContent: '',
      charCount: 0,
    };
    
    // 기존 커스텀 컬럼들에 빈 값 추가
    customColumns.forEach(col => {
      newRecord[col.id] = '';
    });
    
    setRecords(prev => [...prev, newRecord]);
  };

  // 행 삭제
  const handleDeleteRow = (recordId: string) => {
    setRecords(prev => prev.filter(record => record.id !== recordId));
    setMenuAnchor(null);
    setSelectedRecord(null);
  };

  // 새로운 컬럼 추가
  const handleAddColumn = () => {
    if (!newColumnName.trim()) return;
    
    const newColumn: CustomColumn = {
      id: `custom-${Date.now()}`,
      name: newColumnName.trim(),
      type: 'text',
    };
    
    setCustomColumns(prev => [...prev, newColumn]);
    
    // 모든 레코드에 새 컬럼 추가
    setRecords(prev => prev.map(record => ({
      ...record,
      [newColumn.id]: '',
    })));
    
    setNewColumnName('');
    setColumnDialogOpen(false);
  };

  // 컬럼 삭제
  const handleDeleteColumn = (columnId: string) => {
    setCustomColumns(prev => prev.filter(col => col.id !== columnId));
    setRecords(prev => prev.map(record => {
      const { [columnId]: deleted, ...rest } = record;
      return rest as StudentRecord;
    }));
  };

  // AI 생기부 생성
  const handleGenerateContent = async (recordId: string) => {
    const record = records.find(r => r.id === recordId);
    if (!record || !user) return;
    
    // 필수 필드 검증
    if (!record.subject || !record.assignment || !record.studentResult) {
      alert('과목, 과제내용, 학생결과는 필수 입력 사항입니다.');
      return;
    }
    
    setIsGenerating(prev => ({ ...prev, [recordId]: true }));
    
    try {
      // 커스텀 필드 데이터 수집
      const customFields = customColumns.reduce((acc, col) => {
        if (record[col.id]) {
          acc[col.name] = record[col.id];
        }
        return acc;
      }, {} as Record<string, string>);
      
      // 백엔드 API 호출
      const response = await generateAiRecord({
        subject: record.subject,
        assignment: record.assignment,
        student_result: record.studentResult,
        custom_fields: customFields,
        user_email: user.email
      });
      
      if (response.success) {
        setRecords(prev => prev.map(r => 
          r.id === recordId 
            ? { ...r, generatedContent: response.generated_content, charCount: response.char_count }
            : r
        ));
      } else {
        console.error('생기부 생성 실패:', response.error);
        alert(response.error || '생기부 생성 중 오류가 발생했습니다.');
      }
      
    } catch (error) {
      console.error('생기부 생성 중 오류:', error);
      alert('생기부 생성 중 오류가 발생했습니다.');
    } finally {
      setIsGenerating(prev => ({ ...prev, [recordId]: false }));
    }
  };

  // Excel 다운로드
  const handleExcelDownload = () => {
    try {
      // 헤더 생성
      const headers = [
        '학번',
        '학생이름', 
        '과목',
        '과제내용',
        '학생결과',
        ...customColumns.map(col => col.name),
        '생기부 내용',
        '글자수'
      ];

      // 데이터 생성
      const data = records.map(record => [
        record.studentNumber,
        record.studentName,
        record.subject,
        record.assignment,
        record.studentResult,
        ...customColumns.map(col => record[col.id] || ''),
        record.generatedContent,
        record.charCount
      ]);

      // 워크시트 생성
      const worksheet = XLSX.utils.aoa_to_sheet([headers, ...data]);
      
      // 컬럼 너비 설정
      const columnWidths = [
        { wch: 10 }, // 학번
        { wch: 15 }, // 학생이름
        { wch: 12 }, // 과목
        { wch: 30 }, // 과제내용
        { wch: 30 }, // 학생결과
        ...customColumns.map(() => ({ wch: 20 })), // 커스텀 컬럼들
        { wch: 50 }, // 생기부 내용
        { wch: 10 }  // 글자수
      ];
      worksheet['!cols'] = columnWidths;

      // 워크북 생성
      const workbook = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(workbook, worksheet, 'AI 생기부');

      // 파일명 생성 (현재 날짜 포함)
      const today = new Date();
      const dateString = today.toISOString().split('T')[0];
      const fileName = `AI_생기부_${dateString}.xlsx`;

      // 파일 다운로드
      XLSX.writeFile(workbook, fileName);
      
      alert('Excel 파일이 성공적으로 다운로드되었습니다!');
    } catch (error) {
      console.error('Excel 다운로드 중 오류:', error);
      alert('Excel 다운로드 중 오류가 발생했습니다.');
    }
  };

  // 메뉴 열기
  const handleMenuOpen = (event: React.MouseEvent<HTMLElement>, recordId: string) => {
    setMenuAnchor(event.currentTarget);
    setSelectedRecord(recordId);
  };

  // 메뉴 닫기
  const handleMenuClose = () => {
    setMenuAnchor(null);
    setSelectedRecord(null);
  };

  // 생기부 내용 편집 시작
  const handleEditContent = (recordId: string) => {
    const record = records.find(r => r.id === recordId);
    if (!record) return;
    
    setEditingContent(record.generatedContent);
    setEditingContentId(recordId);
    setEditContentDialogOpen(true);
  };

  // 생기부 내용 편집 저장
  const handleSaveContent = () => {
    if (!editingContentId) return;
    
    const charCount = editingContent.length;
    setRecords(prev => prev.map(record => 
      record.id === editingContentId 
        ? { ...record, generatedContent: editingContent, charCount }
        : record
    ));
    
    setEditContentDialogOpen(false);
    setEditingContent('');
    setEditingContentId(null);
  };

  // 생기부 내용 편집 취소
  const handleCancelEditContent = () => {
    setEditContentDialogOpen(false);
    setEditingContent('');
    setEditingContentId(null);
  };

  if (!user || isLoading) {
    return (
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          minHeight: '100vh',
          background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
        }}
      >
        <Box sx={{ textAlign: 'center', color: 'white' }}>
          <CircularProgress sx={{ color: 'white', mb: 2 }} />
          <Typography variant="h6">작업공간을 불러오는 중...</Typography>
        </Box>
      </Box>
    );
  }

  return (
    <Box
      sx={{
        minHeight: '100vh',
        height: '100vh', // 고정 높이 설정
        background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
        position: 'relative',
        display: 'flex',
        flexDirection: 'column',
        '&::before': {
          content: '""',
          position: 'absolute',
          top: 0,
          left: 0,
          width: '100%',
          height: '100%',
          background: 'linear-gradient(45deg, rgba(255,255,255,0.1) 0%, rgba(255,255,255,0.05) 100%)',
          zIndex: 0,
        },
        p: 0,
      }}
    >
      <Box sx={{ position: 'relative', zIndex: 1, width: '100%', display: 'flex', flexDirection: 'column', height: '100%' }}>
        {/* 헤더 */}
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', py: 1, px: 1 }}>
          <Box sx={{ display: 'flex', alignItems: 'center' }}>
            <IconButton
              onClick={() => navigate('/')}
              sx={{
                color: 'white',
                background: 'rgba(255, 255, 255, 0.1)',
                backdropFilter: 'blur(10px)',
                border: '1px solid rgba(255, 255, 255, 0.2)',
                borderRadius: '12px',
                mr: 2,
                '&:hover': {
                  background: 'rgba(255, 255, 255, 0.2)',
                }
              }}
            >
              <ArrowBackIcon />
            </IconButton>
            <SchoolIcon sx={{ color: 'white', mr: 1, fontSize: 32 }} />
            <Typography variant="h4" sx={{ color: 'white', fontWeight: 'bold' }}>
              AI 생기부 작성
            </Typography>
          </Box>
          
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            <Typography variant="body1" sx={{ color: 'white' }}>
              환영합니다, {user.name}님
            </Typography>
            
            {/* 저장 상태 표시 */}
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              {isSaving ? (
                <>
                  <CircularProgress size={16} sx={{ color: 'white' }} />
                  <Typography variant="body2" sx={{ color: 'rgba(255, 255, 255, 0.8)' }}>
                    저장 중...
                  </Typography>
                </>
              ) : (
                <Typography variant="body2" sx={{ color: 'rgba(255, 255, 255, 0.6)' }}>
                  자동 저장됨
                </Typography>
              )}
            </Box>
            
            <Button
              variant="outlined"
              onClick={handleLogout}
              startIcon={<LogoutIcon />}
              sx={{
                color: 'white',
                borderColor: 'rgba(255, 255, 255, 0.3)',
                '&:hover': {
                  borderColor: 'rgba(255, 255, 255, 0.5)',
                  background: 'rgba(255, 255, 255, 0.1)',
                }
              }}
            >
              로그아웃
            </Button>
          </Box>
        </Box>

        {/* 액션 버튼들 */}
        <Box sx={{ display: 'flex', gap: 2, py: 1, px: 1 }}>
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={handleAddRow}
            sx={{
              background: 'linear-gradient(45deg, rgba(76, 175, 80, 0.2), rgba(76, 175, 80, 0.1))',
              backdropFilter: 'blur(10px)',
              border: '1px solid rgba(76, 175, 80, 0.3)',
              borderRadius: '12px',
              color: 'white',
              '&:hover': {
                background: 'linear-gradient(45deg, rgba(76, 175, 80, 0.3), rgba(76, 175, 80, 0.2))',
              }
            }}
          >
            행 추가
          </Button>
          <Button
            variant="contained"
            startIcon={<AddCircleIcon />}
            onClick={() => setColumnDialogOpen(true)}
            sx={{
              background: 'linear-gradient(45deg, rgba(33, 150, 243, 0.2), rgba(33, 150, 243, 0.1))',
              backdropFilter: 'blur(10px)',
              border: '1px solid rgba(33, 150, 243, 0.3)',
              borderRadius: '12px',
              color: 'white',
              '&:hover': {
                background: 'linear-gradient(45deg, rgba(33, 150, 243, 0.3), rgba(33, 150, 243, 0.2))',
              }
            }}
          >
            칼럼 추가
          </Button>
          <Button
            variant="contained"
            startIcon={isSaving ? <CircularProgress size={16} /> : <SaveIcon />}
            onClick={saveWorkspaceData}
            disabled={isSaving}
            sx={{
              background: 'linear-gradient(45deg, rgba(156, 39, 176, 0.2), rgba(156, 39, 176, 0.1))',
              backdropFilter: 'blur(10px)',
              border: '1px solid rgba(156, 39, 176, 0.3)',
              borderRadius: '12px',
              color: 'white',
              '&:hover': {
                background: 'linear-gradient(45deg, rgba(156, 39, 176, 0.3), rgba(156, 39, 176, 0.2))',
              },
              '&:disabled': {
                background: 'rgba(255, 255, 255, 0.1)',
                color: 'rgba(255, 255, 255, 0.5)',
              }
            }}
          >
            {isSaving ? '저장 중' : '저장'}
          </Button>
          <Button
            variant="contained"
            startIcon={<DownloadIcon />}
            onClick={handleExcelDownload}
            sx={{
              background: 'linear-gradient(45deg, rgba(255, 152, 0, 0.2), rgba(255, 152, 0, 0.1))',
              backdropFilter: 'blur(10px)',
              border: '1px solid rgba(255, 152, 0, 0.3)',
              borderRadius: '12px',
              color: 'white',
              '&:hover': {
                background: 'linear-gradient(45deg, rgba(255, 152, 0, 0.3), rgba(255, 152, 0, 0.2))',
              }
            }}
          >
            Excel 다운로드
          </Button>
        </Box>

        {/* 테이블 */}
        <Box sx={{ flex: 1, overflow: 'hidden', px: 1, pb: 1 }}>
          <TableContainer 
            component={Paper}
            sx={{
              background: 'rgba(255, 255, 255, 0.1)',
              backdropFilter: 'blur(20px)',
              border: '1px solid rgba(255, 255, 255, 0.2)',
              borderRadius: '16px',
              height: '100%',
              overflow: 'auto',
              '&::-webkit-scrollbar': {
                width: '8px',
                height: '8px',
              },
              '&::-webkit-scrollbar-track': {
                background: 'rgba(255, 255, 255, 0.1)',
              },
              '&::-webkit-scrollbar-thumb': {
                background: 'rgba(255, 255, 255, 0.3)',
                borderRadius: '4px',
              },
            }}
          >
            <Table sx={{ minWidth: 1200 }}>
              <TableHead>
                <TableRow>
                  <TableCell sx={{ 
                    background: 'rgba(255, 255, 255, 0.2)', 
                    backdropFilter: 'blur(10px)',
                    boxShadow: '0 2px 4px rgba(0, 0, 0, 0.1)',
                    color: 'white', 
                    fontWeight: 'bold', 
                    minWidth: 80,
                    borderRight: '1px solid rgba(255, 255, 255, 0.2)',
                    position: 'sticky',
                    top: 0,
                    zIndex: 100
                  }}>
                    학번
                  </TableCell>
                  <TableCell sx={{ 
                    background: 'rgba(255, 255, 255, 0.2)', 
                    backdropFilter: 'blur(10px)',
                    boxShadow: '0 2px 4px rgba(0, 0, 0, 0.1)',
                    color: 'white', 
                    fontWeight: 'bold', 
                    minWidth: 100,
                    borderRight: '1px solid rgba(255, 255, 255, 0.2)',
                    position: 'sticky',
                    top: 0,
                    zIndex: 100
                  }}>
                    학생이름
                  </TableCell>
                  <TableCell sx={{ 
                    background: 'rgba(255, 255, 255, 0.2)', 
                    backdropFilter: 'blur(10px)',
                    boxShadow: '0 2px 4px rgba(0, 0, 0, 0.1)',
                    color: 'white', 
                    fontWeight: 'bold', 
                    minWidth: 100,
                    borderRight: '1px solid rgba(255, 255, 255, 0.2)',
                    position: 'sticky',
                    top: 0,
                    zIndex: 100
                  }}>
                    과목
                  </TableCell>
                  <TableCell sx={{ 
                    background: 'rgba(255, 255, 255, 0.2)', 
                    backdropFilter: 'blur(10px)',
                    boxShadow: '0 2px 4px rgba(0, 0, 0, 0.1)',
                    color: 'white', 
                    fontWeight: 'bold', 
                    minWidth: 200,
                    borderRight: '1px solid rgba(255, 255, 255, 0.2)',
                    position: 'sticky',
                    top: 0,
                    zIndex: 100
                  }}>
                    과제내용
                  </TableCell>
                  <TableCell sx={{ 
                    background: 'rgba(255, 255, 255, 0.2)', 
                    backdropFilter: 'blur(10px)',
                    boxShadow: '0 2px 4px rgba(0, 0, 0, 0.1)',
                    color: 'white', 
                    fontWeight: 'bold', 
                    minWidth: 200,
                    borderRight: '1px solid rgba(255, 255, 255, 0.2)',
                    position: 'sticky',
                    top: 0,
                    zIndex: 100
                  }}>
                    학생결과
                  </TableCell>
                  
                  {/* 동적 컬럼들 */}
                  {customColumns.map(col => (
                    <TableCell 
                      key={col.id} 
                      sx={{ 
                        background: 'rgba(255, 255, 255, 0.2)', 
                        backdropFilter: 'blur(10px)',
                        boxShadow: '0 2px 4px rgba(0, 0, 0, 0.1)',
                        color: 'white', 
                        fontWeight: 'bold',
                        minWidth: 150,
                        position: 'sticky',
                        top: 0,
                        zIndex: 100,
                        borderRight: '1px solid rgba(255, 255, 255, 0.2)'
                      }}
                    >
                      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                        {col.name}
                        <IconButton
                          size="small"
                          onClick={() => handleDeleteColumn(col.id)}
                          sx={{ color: 'rgba(255, 255, 255, 0.7)', ml: 1 }}
                        >
                          <DeleteIcon fontSize="small" />
                        </IconButton>
                      </Box>
                    </TableCell>
                  ))}
                  
                  <TableCell sx={{ 
                    background: 'rgba(255, 255, 255, 0.2)', 
                    backdropFilter: 'blur(10px)',
                    boxShadow: '0 2px 4px rgba(0, 0, 0, 0.1)',
                    color: 'white', 
                    fontWeight: 'bold', 
                    minWidth: 100,
                    borderRight: '1px solid rgba(255, 255, 255, 0.2)',
                    position: 'sticky',
                    top: 0,
                    zIndex: 100
                  }}>
                    생성
                  </TableCell>
                  <TableCell sx={{ 
                    background: 'rgba(255, 255, 255, 0.2)', 
                    backdropFilter: 'blur(10px)',
                    boxShadow: '0 2px 4px rgba(0, 0, 0, 0.1)',
                    color: 'white', 
                    fontWeight: 'bold', 
                    minWidth: 300,
                    borderRight: '1px solid rgba(255, 255, 255, 0.2)',
                    position: 'sticky',
                    top: 0,
                    zIndex: 100
                  }}>
                    생기부 내용
                  </TableCell>
                  <TableCell sx={{ 
                    background: 'rgba(255, 255, 255, 0.2)', 
                    backdropFilter: 'blur(10px)',
                    boxShadow: '0 2px 4px rgba(0, 0, 0, 0.1)',
                    color: 'white', 
                    fontWeight: 'bold', 
                    minWidth: 80,
                    borderRight: '1px solid rgba(255, 255, 255, 0.2)',
                    position: 'sticky',
                    top: 0,
                    zIndex: 100
                  }}>
                    글자수
                  </TableCell>
                  <TableCell sx={{ 
                    background: 'rgba(255, 255, 255, 0.2)', 
                    backdropFilter: 'blur(10px)',
                    boxShadow: '0 2px 4px rgba(0, 0, 0, 0.1)',
                    color: 'white', 
                    fontWeight: 'bold', 
                    minWidth: 60,
                    position: 'sticky',
                    top: 0,
                    zIndex: 100
                  }}>
                    액션
                  </TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {records.map((record) => (
                  <TableRow key={record.id}>
                                         {/* 기본 컬럼들 */}
                     {['studentNumber', 'studentName', 'subject', 'assignment', 'studentResult'].map(field => (
                       <TableCell key={field} sx={{ 
                         color: 'white', 
                         borderBottom: '1px solid rgba(255, 255, 255, 0.1)',
                         borderRight: '1px solid rgba(255, 255, 255, 0.1)'
                       }}>
                        {editingCell?.recordId === record.id && editingCell?.field === field ? (
                          <TextField
                            value={tempValue}
                            onChange={(e) => setTempValue(e.target.value)}
                            onBlur={handleCellSave}
                            onKeyDown={(e) => {
                              if (e.key === 'Enter' && !e.shiftKey) {
                                // multiline 필드에서는 Shift+Enter로 저장
                                if (field === 'assignment' || field === 'studentResult') {
                                  // 기본 Enter는 줄바꿈
                                  return;
                                } else {
                                  handleCellSave();
                                }
                              }
                              if (e.key === 'Enter' && e.shiftKey) {
                                // multiline 필드에서 Shift+Enter로 저장
                                if (field === 'assignment' || field === 'studentResult') {
                                  e.preventDefault();
                                  handleCellSave();
                                }
                              }
                              if (e.key === 'Escape') handleCellCancel();
                              if (e.key === 'Tab') {
                                e.preventDefault();
                                if (e.shiftKey) {
                                  moveToPrevCell();
                                } else {
                                  moveToNextCell();
                                }
                              }
                            }}
                            autoFocus
                            size="small"
                            multiline={field === 'assignment' || field === 'studentResult'}
                            rows={field === 'assignment' || field === 'studentResult' ? 2 : 1}
                            sx={{
                              '& .MuiOutlinedInput-root': {
                                color: 'white',
                                '& fieldset': {
                                  borderColor: 'rgba(255, 255, 255, 0.3)',
                                },
                              },
                            }}
                          />
                        ) : (
                          <Box
                            onClick={() => handleCellEdit(record.id, field, record[field])}
                            sx={{
                              cursor: 'pointer',
                              p: 1,
                              borderRadius: '4px',
                              minHeight: '20px',
                              '&:hover': {
                                background: 'rgba(255, 255, 255, 0.1)',
                              },
                            }}
                          >
                            {record[field] || '클릭하여 입력'}
                          </Box>
                        )}
                      </TableCell>
                    ))}
                    
                                         {/* 동적 컬럼들 */}
                     {customColumns.map(col => (
                       <TableCell key={col.id} sx={{ 
                         color: 'white', 
                         borderBottom: '1px solid rgba(255, 255, 255, 0.1)',
                         borderRight: '1px solid rgba(255, 255, 255, 0.1)'
                       }}>
                        {editingCell?.recordId === record.id && editingCell?.field === col.id ? (
                          <TextField
                            value={tempValue}
                            onChange={(e) => setTempValue(e.target.value)}
                            onBlur={handleCellSave}
                            onKeyDown={(e) => {
                              if (e.key === 'Enter') handleCellSave();
                              if (e.key === 'Escape') handleCellCancel();
                              if (e.key === 'Tab') {
                                e.preventDefault();
                                if (e.shiftKey) {
                                  moveToPrevCell();
                                } else {
                                  moveToNextCell();
                                }
                              }
                            }}
                            autoFocus
                            size="small"
                            sx={{
                              '& .MuiOutlinedInput-root': {
                                color: 'white',
                                '& fieldset': {
                                  borderColor: 'rgba(255, 255, 255, 0.3)',
                                },
                              },
                            }}
                          />
                        ) : (
                          <Box
                            onClick={() => handleCellEdit(record.id, col.id, record[col.id] || '')}
                            sx={{
                              cursor: 'pointer',
                              p: 1,
                              borderRadius: '4px',
                              minHeight: '20px',
                              '&:hover': {
                                background: 'rgba(255, 255, 255, 0.1)',
                              },
                            }}
                          >
                            {record[col.id] || '클릭하여 입력'}
                          </Box>
                        )}
                      </TableCell>
                    ))}
                    
                                         {/* 생성 버튼 */}
                     <TableCell sx={{ 
                       color: 'white', 
                       borderBottom: '1px solid rgba(255, 255, 255, 0.1)',
                       borderRight: '1px solid rgba(255, 255, 255, 0.1)'
                     }}>
                       <Button
                         variant="contained"
                         size="small"
                         onClick={() => handleGenerateContent(record.id)}
                         disabled={isGenerating[record.id] || !record.subject || !record.assignment || !record.studentResult}
                         startIcon={isGenerating[record.id] ? <CircularProgress size={16} /> : <GenerateIcon />}
                         sx={{
                           background: 'linear-gradient(45deg, rgba(156, 39, 176, 0.2), rgba(156, 39, 176, 0.1))',
                           border: '1px solid rgba(156, 39, 176, 0.3)',
                           borderRadius: '8px',
                           color: 'white',
                           minWidth: '80px',
                           '&:hover': {
                             background: 'linear-gradient(45deg, rgba(156, 39, 176, 0.3), rgba(156, 39, 176, 0.2))',
                           },
                           '&:disabled': {
                             background: 'rgba(255, 255, 255, 0.1)',
                             color: 'rgba(255, 255, 255, 0.5)',
                           }
                         }}
                       >
                         {isGenerating[record.id] ? '생성중' : '생성'}
                       </Button>
                     </TableCell>
                     
                     {/* 생기부 내용 */}
                     <TableCell sx={{ 
                       color: 'white', 
                       borderBottom: '1px solid rgba(255, 255, 255, 0.1)',
                       borderRight: '1px solid rgba(255, 255, 255, 0.1)'
                     }}>
                       <Box sx={{ display: 'flex', alignItems: 'start', gap: 1 }}>
                         <Box
                           sx={{
                             maxWidth: '250px',
                             maxHeight: '100px',
                             overflow: 'auto',
                             p: 1,
                             background: 'rgba(255, 255, 255, 0.05)',
                             borderRadius: '4px',
                             whiteSpace: 'pre-line',
                             fontSize: '0.875rem',
                             lineHeight: 1.4,
                             flex: 1,
                           }}
                         >
                           {record.generatedContent || '생성 버튼을 클릭하여 생기부를 생성하세요.'}
                         </Box>
                         {record.generatedContent && (
                           <IconButton
                             size="small"
                             onClick={() => handleEditContent(record.id)}
                             sx={{ 
                               color: 'rgba(255, 255, 255, 0.7)',
                               '&:hover': {
                                 color: 'white',
                                 background: 'rgba(255, 255, 255, 0.1)',
                               }
                             }}
                           >
                             <EditIcon fontSize="small" />
                           </IconButton>
                         )}
                       </Box>
                     </TableCell>
                     
                     {/* 글자수 */}
                     <TableCell sx={{ 
                       color: 'white', 
                       borderBottom: '1px solid rgba(255, 255, 255, 0.1)',
                       borderRight: '1px solid rgba(255, 255, 255, 0.1)'
                     }}>
                       <Chip
                         label={`${record.charCount}/500`}
                         size="small"
                         sx={{
                           background: record.charCount > 500 
                             ? 'rgba(244, 67, 54, 0.2)' 
                             : record.charCount > 0 
                               ? 'rgba(76, 175, 80, 0.2)' 
                               : 'rgba(255, 255, 255, 0.1)',
                           color: record.charCount > 500 ? '#ff6b6b' : 'white',
                           border: `1px solid ${record.charCount > 500 ? 'rgba(244, 67, 54, 0.5)' : 'rgba(255, 255, 255, 0.2)'}`,
                         }}
                       />
                     </TableCell>
                     
                     {/* 액션 */}
                     <TableCell sx={{ 
                       color: 'white', 
                       borderBottom: '1px solid rgba(255, 255, 255, 0.1)'
                     }}>
                       <IconButton
                         size="small"
                         onClick={(e) => handleMenuOpen(e, record.id)}
                         sx={{ color: 'white' }}
                       >
                         <MoreVertIcon />
                       </IconButton>
                     </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>

          {/* 행 메뉴 */}
          <Menu
            anchorEl={menuAnchor}
            open={Boolean(menuAnchor)}
            onClose={handleMenuClose}
            PaperProps={{
              sx: {
                background: 'rgba(255, 255, 255, 0.1)',
                backdropFilter: 'blur(20px)',
                border: '1px solid rgba(255, 255, 255, 0.2)',
                borderRadius: '12px',
                color: 'white',
              }
            }}
          >
            <MenuItem onClick={() => selectedRecord && handleDeleteRow(selectedRecord)}>
              <DeleteIcon sx={{ mr: 1 }} />
              행 삭제
            </MenuItem>
          </Menu>

          {/* 컬럼 추가 다이얼로그 */}
          <Dialog
            open={columnDialogOpen}
            onClose={() => setColumnDialogOpen(false)}
            maxWidth="sm"
            fullWidth
            PaperProps={{
              sx: {
                background: 'rgba(255, 255, 255, 0.1)',
                backdropFilter: 'blur(20px)',
                border: '1px solid rgba(255, 255, 255, 0.2)',
                borderRadius: '16px',
                color: 'white',
              }
            }}
          >
            <DialogTitle sx={{ color: 'white' }}>
              새 컬럼 추가
            </DialogTitle>
            <DialogContent>
              <TextField
                autoFocus
                fullWidth
                label="컬럼 이름"
                value={newColumnName}
                onChange={(e) => setNewColumnName(e.target.value)}
                sx={{
                  mt: 2,
                  '& .MuiOutlinedInput-root': {
                    color: 'white',
                    '& fieldset': {
                      borderColor: 'rgba(255, 255, 255, 0.3)',
                    },
                    '&:hover fieldset': {
                      borderColor: 'rgba(255, 255, 255, 0.5)',
                    },
                    '&.Mui-focused fieldset': {
                      borderColor: '#2196f3',
                    },
                  },
                  '& .MuiInputLabel-root': {
                    color: 'rgba(255, 255, 255, 0.7)',
                    '&.Mui-focused': {
                      color: '#2196f3',
                    },
                  },
                }}
              />
            </DialogContent>
            <DialogActions>
              <Button 
                onClick={() => setColumnDialogOpen(false)}
                sx={{ color: 'white' }}
              >
                취소
              </Button>
              <Button 
                onClick={handleAddColumn}
                disabled={!newColumnName.trim()}
                variant="contained"
                sx={{
                  background: 'linear-gradient(45deg, rgba(33, 150, 243, 0.2), rgba(33, 150, 243, 0.1))',
                  border: '1px solid rgba(33, 150, 243, 0.3)',
                  '&:hover': {
                    background: 'linear-gradient(45deg, rgba(33, 150, 243, 0.3), rgba(33, 150, 243, 0.2))',
                  },
                }}
              >
                추가
              </Button>
            </DialogActions>
          </Dialog>

          {/* 생기부 내용 편집 다이얼로그 */}
          <Dialog
            open={editContentDialogOpen}
            onClose={handleCancelEditContent}
            maxWidth="md"
            fullWidth
            PaperProps={{
              sx: {
                background: 'rgba(255, 255, 255, 0.1)',
                backdropFilter: 'blur(20px)',
                border: '1px solid rgba(255, 255, 255, 0.2)',
                borderRadius: '16px',
                color: 'white',
              }
            }}
          >
            <DialogTitle sx={{ color: 'white', display: 'flex', alignItems: 'center', gap: 1 }}>
              <EditIcon />
              생기부 내용 편집
            </DialogTitle>
            <DialogContent>
              <TextField
                autoFocus
                fullWidth
                multiline
                rows={12}
                label="생기부 내용"
                value={editingContent}
                onChange={(e) => setEditingContent(e.target.value)}
                helperText={`${editingContent.length}/500자 (${editingContent.length > 500 ? '초과' : '적정'})`}
                sx={{
                  mt: 2,
                  '& .MuiOutlinedInput-root': {
                    color: 'white',
                    '& fieldset': {
                      borderColor: 'rgba(255, 255, 255, 0.3)',
                    },
                    '&:hover fieldset': {
                      borderColor: 'rgba(255, 255, 255, 0.5)',
                    },
                    '&.Mui-focused fieldset': {
                      borderColor: '#2196f3',
                    },
                  },
                  '& .MuiInputLabel-root': {
                    color: 'rgba(255, 255, 255, 0.7)',
                    '&.Mui-focused': {
                      color: '#2196f3',
                    },
                  },
                  '& .MuiFormHelperText-root': {
                    color: editingContent.length > 500 ? '#ff6b6b' : 'rgba(255, 255, 255, 0.7)',
                  },
                }}
              />
            </DialogContent>
            <DialogActions>
              <Button 
                onClick={handleCancelEditContent}
                sx={{ color: 'white' }}
              >
                취소
              </Button>
              <Button 
                onClick={handleSaveContent}
                variant="contained"
                sx={{
                  background: 'linear-gradient(45deg, rgba(76, 175, 80, 0.2), rgba(76, 175, 80, 0.1))',
                  border: '1px solid rgba(76, 175, 80, 0.3)',
                  '&:hover': {
                    background: 'linear-gradient(45deg, rgba(76, 175, 80, 0.3), rgba(76, 175, 80, 0.2))',
                  },
                }}
              >
                저장
              </Button>
            </DialogActions>
          </Dialog>
        </Box>
      </Box>
    </Box>
  );
};

export default AiRecordPage; 